import React, { useEffect, useRef, useState } from "react";

import { isSameDay } from "date-fns";
import { makeStyles } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import {
  calendarActions,
  selectCalendarInitialDate,
  selectCalendarView,
} from "@hubcapp/state";
import { useResize } from "@hubcapp/hooks";
import { useTealium } from "@hubcapp/context";

import { CalendarView, PossibleSelections } from "@ds-developer/models";
import { Scroller } from "@ds-developer/calendar";

import { DateData, ScrollerStateProviderProps } from "./types";

import { getScrollIndex, getSelectedDateIndex, isDateActive } from "./helpers";

import {
  generateDays,
  generateMonths,
  offset,
  selection,
} from "@ds-developer/calendar";

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: "fixed",
    left: "0",
    right: "0",
    backgroundColor: "#f4f6f8",
    zIndex: 10,
    [theme.breakpoints.up("xs")]: {
      top: "120px",
    },
    [theme.breakpoints.up("sm")]: {
      top: "193px",
    },
    [theme.breakpoints.up("md")]: {
      position: "initial",
      top: "auto",
      left: "auto",
      zIndex: "initial",
    },
  },
}));

const SCROLLER_MAX_WIDTH = 1050;

const viewSelectionLengthMap: Record<string, number> = {
  [CalendarView.day]: 0,
  [CalendarView.threeDay]: 1,
  [CalendarView.week]: 3.5,
  [CalendarView.month]: 2.5,
};

const ScrollerStateProvider: React.FC<ScrollerStateProviderProps> = ({
  calendarView,
  dateProvided,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const scrollerWrapperRef = useRef<HTMLDivElement>(null);
  const { width } = useResize<HTMLDivElement>(scrollerWrapperRef);

  const view = calendarView ?? useSelector(selectCalendarView);
  const date = dateProvided ?? useSelector(selectCalendarInitialDate);

  const { trackEvent } = useTealium();
  const monthView = view === "MONTH";

  const [dateData, setDateData] = useState<DateData>(
    monthView
      ? generateMonths(date ?? undefined)
      : generateDays(selection[view] as PossibleSelections, date ?? undefined),
  );

  const [scrollToIndex, setScrollToIndex] = useState<number>(365);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const setDate = (newDate?: Date) => {
    dispatch(
      calendarActions.calendarInitialDateChanged({ initialDate: newDate }),
    );
  };

  const handleDateChange = (index: number, enforceFutureDate = false) => {
    const selectedDate = new Date(dateData.entries[index].date);
    const updatedDateData = {
      ...dateData,
      current: index,
      entries: dateData.entries.map(entry => ({
        ...entry,
        selected: isDateActive(
          selectedDate,
          new Date(entry.date),
          view,
          enforceFutureDate,
        ),
      })),
    };

    setDate(
      updatedDateData.entries[getSelectedDateIndex(updatedDateData)].date,
    );
    setDateData(updatedDateData);
    setScrollToIndex(
      getScrollIndex(updatedDateData, viewSelectionLengthMap[view]),
    );
  };

  useEffect(() => {
    handleDateChange(dateData.current, true);
  }, [view]);

  useEffect(() => {
    setScrollToIndex(getScrollIndex(dateData, viewSelectionLengthMap[view]));
  }, [width]);

  useEffect(() => {
    if (!date) return;

    // We need to derive the index of the selected date in order to use existing logic.
    // To be refactored in tech debt ticket DST-1935
    const dateIndex = dateData.entries.findIndex(entry =>
      isSameDay(new Date(entry.date), new Date(date)),
    );

    handleDateChange(dateIndex);
  }, [date]);

  const onForwardClick = (index: number) => {
    trackEvent({
      eventCategory: "Calendar",
      eventAction: "Button Click",
      eventLabel: "Forward",
    });
    handleDateChange(index - offset[view]);
  };
  const onBackwardClick = (index: number) => {
    trackEvent({
      eventCategory: "Calendar",
      eventAction: "Button Click",
      eventLabel: "Back",
    });
    handleDateChange(index - offset[view]);
  };

  const handleDateClicked = (index: number) => {
    const isSelected = dateData.entries[index].selected;

    if (!isSelected) {
      handleDateChange(index);
    }
  };

  return (
    <div ref={scrollerWrapperRef} className={classes.wrapper}>
      <Scroller
        view={view}
        dateData={dateData}
        scrollToIndex={scrollToIndex}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        onDateClick={handleDateClicked}
        onForwardClick={onForwardClick}
        onBackwardClick={onBackwardClick}
        monthView={monthView}
        width={width <= SCROLLER_MAX_WIDTH ? width : SCROLLER_MAX_WIDTH}
      />
    </div>
  );
};

export default ScrollerStateProvider;
